import { 
  getListaByIdLocal, getMultiStoreQuotesForList, cleanProductDescription, optimizeShoppingListBackend 
} from '../db.js';
import { escapeHTML } from '../security.js';
import { authManager } from '../auth-manager.js';
import { getCategoryIcon } from '../categorizer.js';

function formatBRL(val) {
  return 'R$ ' + (Number(val) || 0).toFixed(2).replace('.', ',');
}

function formatStoreShortName(name, maxWords = 2) {
  if (!name) return '';
  const trimmed = String(name).trim();
  const cleaned = trimmed.replace(/\b(ltda|s\/?a|s\.a|eireli|me|epp)\b\.?/gi, '').trim();
  const words = (cleaned || trimmed).split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return cleaned || trimmed;
  return words.slice(0, maxWords).join(' ');
}

export async function renderListaComparativoView(container, id, syncManager) {
  if (!id) {
    window.location.hash = '#listas';
    return;
  }

  let lista = await getListaByIdLocal(Number(id)).catch(() => null);

  if (!lista && authManager.isAuthenticated()) {
    container.innerHTML = `
      <div class="chart-card text-center" style="padding: 3rem 1.5rem; margin-top: 1.5rem; border-radius: 1rem;">
        <div style="width: 42px; height: 42px; border: 3.5px solid var(--border-color); border-top-color: var(--color-primary, #2563EB); border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 1.25rem auto;"></div>
        <h3 style="font-size: 1.15rem; font-weight: 800; color: var(--text-main); margin-bottom: 0.35rem;">Carregando Comparativo...</h3>
        <p class="text-muted" style="font-size: 0.85rem; margin: 0;">Analisando preços e supermercados...</p>
      </div>
    `;

    try {
      const res = await authManager.fetchWithAuth(`/api/listas/${id}`);
      if (res.ok) {
        lista = await res.json();
      }
    } catch (e) {
      console.warn('[renderListaComparativoView] Fetch error:', e);
    }
  }

  if (!lista) {
    container.innerHTML = `
      <div class="chart-card text-center" style="padding: 2.5rem 1.5rem; margin-top: 1.5rem;">
        <span class="material-symbols-outlined" style="font-size: 3.5rem; color: #94A3B8;">checklist</span>
        <h3 style="font-size: 1.2rem; font-weight: 700; color: #111827; margin: 0.5rem 0;">Lista não encontrada</h3>
        <p class="text-muted" style="font-size: 0.875rem; margin-bottom: 1.5rem;">A lista solicitada não existe ou foi removida.</p>
        <a href="#listas" class="btn btn-primary" style="border-radius: var(--radius-pill); font-weight: 700; display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.6rem 1.2rem;">
          <span class="material-symbols-outlined">arrow_back</span> Voltar para Listas
        </a>
      </div>
    `;
    return;
  }

  const listTitle = lista.name || lista.nome || 'Lista de Compras';
  window.dispatchEvent(new CustomEvent('nfce-page-title-changed', { detail: { title: `Comparativo: ${listTitle}` } }));

  const backOrigin = sessionStorage.getItem('comparativo_origin_hash') || `#lista/${id}`;

  // Fetch matrix & rankings directly without spinning sync card
  try {
    const multiData = await getMultiStoreQuotesForList(lista.items || []);
    const itemsToOptimize = (lista.items || []).map(it => ({
      ean: it.barcode || it.ean || '',
      quantidade: it.quantity || it.quantidade || 1
    })).filter(it => it.ean && it.ean.trim().length > 0);

    let backendRanking = [];
    if (itemsToOptimize.length > 0) {
      const res = await optimizeShoppingListBackend(itemsToOptimize).catch(() => ({ ranking: [] }));
      backendRanking = res.ranking || [];
    }

    const hasLocalData = multiData && multiData.ranking && multiData.ranking.length > 0;
    const hasBackendData = backendRanking.length > 0;

    if (!hasLocalData && !hasBackendData) {
      container.innerHTML = `
        <div id="lista-comparativo-wrapper" style="padding-bottom: 95px;">
          <div class="detail-header" style="margin-bottom: 1rem;">
            <div style="display: flex; align-items: center; gap: 0.65rem; width: 100%;">
              <a href="${backOrigin}" class="detail-back" style="background: var(--bg-surface-dim); border: none; color: var(--text-main); cursor: pointer; padding: 0.45rem; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; text-decoration: none;">
                <span class="material-symbols-outlined" style="font-size: 20px;">arrow_back</span>
              </a>
              <div style="min-width: 0; flex: 1;">
                <h2 style="margin: 0; font-size: clamp(1.15rem, 4vw, 1.45rem); font-weight: 800; color: var(--text-main); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                  ${escapeHTML(listTitle)}
                </h2>
                <div style="font-size: 0.75rem; color: var(--text-muted);">
                  Comparativo de Preços
                </div>
              </div>
            </div>
          </div>

          <div class="chart-card text-center text-muted" style="padding: 3rem 1.5rem; border-radius: 1rem; margin-bottom: 1.5rem;">
            <span class="material-symbols-outlined" style="font-size: 48px; opacity: 0.35; display: block; margin-bottom: 0.75rem;">storefront</span>
            <h3 style="font-size: 1.15rem; font-weight: 800; color: var(--text-main); margin-bottom: 0.5rem;">Nenhuma cotação registrada ainda</h3>
            <p style="font-size: 0.85rem; margin-bottom: 1.5rem; max-width: 320px; margin-left: auto; margin-right: auto; line-height: 1.45;">
              Acesse a tela de cotação para escanear etiquetas ou digitar os preços nos supermercados onde você costuma comprar.
            </p>
            <a href="#lista-cotacao/${id}" class="btn btn-primary" style="border-radius: var(--radius-pill); font-weight: 700; display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.65rem 1.25rem; text-decoration: none;">
              <span class="material-symbols-outlined">photo_camera</span>
              <span>Abrir Cotação na Gôndola &rarr;</span>
            </a>
          </div>
        </div>
      `;
      return;
    }

    const ranking = hasLocalData ? multiData.ranking : backendRanking.map(b => ({
      storeName: b.Mercado,
      total: b.TotalEstimado,
      itemsQuoted: b.ItensAtendidos,
      totalItems: b.TotalItens,
      coveragePercent: Math.round(b.PercentAtendido || 0),
      items: (b.Itens || []).map(it => ({
        item: { description: it.Descricao },
        unitPrice: it.PrecoUnitario,
        totalPrice: it.PrecoTotal,
        available: it.Disponivel
      }))
    }));

    const mixedBasket = multiData?.mixedBasket;
    const bestSingleTotal = ranking[0]?.total || 0;
    const mixedSavings = mixedBasket && bestSingleTotal > mixedBasket.total ? (bestSingleTotal - mixedBasket.total) : 0;
    const mixedSavingsPct = bestSingleTotal > 0 && mixedSavings > 0 ? Math.round((mixedSavings / bestSingleTotal) * 100) : 0;

    // Group mixed basket items by best store
    const mixedByStore = {};
    if (mixedBasket && Array.isArray(mixedBasket.items)) {
      mixedBasket.items.forEach(it => {
        const st = it.bestStore || 'Estimativa Geral';
        if (!mixedByStore[st]) mixedByStore[st] = [];
        mixedByStore[st].push(it);
      });
    }

    container.innerHTML = `
      <div id="lista-comparativo-wrapper" style="padding-bottom: 95px; transition: padding-bottom 0.15s ease;">
        
        <!-- Header & Back Button to Cotacao Screen -->
        <div class="detail-header" style="margin-bottom: 1rem;">
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; width: 100%;">
            <div style="display: flex; align-items: center; gap: 0.65rem; min-width: 0; flex: 1;">
              <a href="${backOrigin}" class="detail-back" id="btn-back-to-cotacao" style="background: var(--bg-surface-dim); border: none; color: var(--text-main); cursor: pointer; padding: 0.45rem; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; text-decoration: none;">
                <span class="material-symbols-outlined" style="font-size: 20px;">arrow_back</span>
              </a>
              <div style="min-width: 0; flex: 1;">
                <h2 style="margin: 0; font-size: clamp(1.15rem, 4vw, 1.45rem); font-weight: 800; color: var(--text-main); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                  ${escapeHTML(listTitle)}
                </h2>
                <div style="font-size: 0.75rem; color: var(--text-muted);">
                  Comparativo de Preços &amp; Otimizador de Economia
                </div>
              </div>
            </div>

            <a href="#lista-cotacao/${id}" class="btn" style="height: 36px; padding: 0 0.85rem; border-radius: var(--radius-pill); font-size: 0.78rem; font-weight: 700; border: 1px solid var(--border-color); background: var(--bg-surface-alt); color: var(--text-main); display: inline-flex; align-items: center; gap: 0.3rem; text-decoration: none; white-space: nowrap;">
              <span class="material-symbols-outlined" style="font-size: 16px; color: var(--color-primary-text, #2563EB);">photo_camera</span>
              <span>Cotar</span>
            </a>
          </div>
        </div>

        <!-- 1. Hero Insights: Divisão Inteligente (Cesta Mista) -->
        ${mixedSavings > 0.5 ? `
          <div class="chart-card" style="margin-bottom: 1.25rem; padding: 1.25rem; background: rgba(16, 185, 129, 0.08); border: 1.5px solid #10B981; border-radius: 1.15rem; box-shadow: var(--shadow-card);">
            <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.65rem; flex-wrap: wrap; gap: 0.5rem;">
              <div style="display: flex; align-items: center; gap: 0.45rem;">
                <div style="width: 36px; height: 36px; border-radius: 50%; background: #10B981; color: #FFF; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                  <span class="material-symbols-outlined" style="font-size: 20px;">savings</span>
                </div>
                <div>
                  <span style="font-size: 0.72rem; font-weight: 800; color: #047857; text-transform: uppercase; letter-spacing: 0.05em; display: block;">Economia Máxima</span>
                  <strong style="font-size: 1.05rem; color: var(--text-main);">Divisão Inteligente (Cesta Mista)</strong>
                </div>
              </div>
              <div style="text-align: right;">
                <span class="font-mono font-bold" style="font-size: 1.45rem; color: #047857; display: block; line-height: 1;">
                  ${formatBRL(mixedBasket.total)}
                </span>
                <span style="font-size: 0.72rem; font-weight: 800; color: #047857; background: rgba(16, 185, 129, 0.15); padding: 0.15rem 0.5rem; border-radius: var(--radius-pill); display: inline-block; margin-top: 0.2rem;">
                  - ${formatBRL(mixedSavings)} (${mixedSavingsPct}%)
                </span>
              </div>
            </div>

            <p style="font-size: 0.82rem; color: var(--text-main); margin: 0 0 0.85rem 0; line-height: 1.45;">
              Comprando cada item no supermercado onde ele está mais barato, você economiza <strong>${formatBRL(mixedSavings)}</strong> em comparação a fazer toda a compra no estabelecimento #1!
            </p>

            <!-- Store-by-Store Mixed Distribution Breakdown -->
            <div style="display: flex; flex-direction: column; gap: 0.5rem;">
              <span style="font-size: 0.72rem; font-weight: 800; color: var(--text-muted); text-transform: uppercase;">
                Onde Comprar Cada Produto:
              </span>
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 0.5rem;">
                ${Object.entries(mixedByStore).map(([stName, stItems]) => {
                  const subTotal = stItems.reduce((acc, x) => acc + x.totalPrice, 0);
                  return `
                    <div style="background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 0.75rem; padding: 0.65rem 0.85rem; display: flex; flex-direction: column; justify-content: space-between;">
                      <div>
                        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.35rem;">
                          <strong style="font-size: 0.85rem; color: var(--text-main);">🏪 ${escapeHTML(stName)}</strong>
                          <span class="font-mono font-bold" style="font-size: 0.85rem; color: #10B981;">${formatBRL(subTotal)}</span>
                        </div>
                        <div style="display: flex; flex-direction: column; gap: 0.2rem; font-size: 0.73rem; color: var(--text-muted); margin-bottom: 0.5rem;">
                          ${stItems.map(it => {
                            const desc = cleanProductDescription(it.item?.description || it.item?.descricao || 'Item');
                            return `
                              <div style="display: flex; justify-content: space-between;">
                                <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 140px;">• ${escapeHTML(desc)}</span>
                                <span class="font-mono">${formatBRL(it.totalPrice)}</span>
                              </div>
                            `;
                          }).join('')}
                        </div>
                      </div>

                      <a href="#lista/${id}" class="btn-open-store-split" data-store="${escapeHTML(stName)}" style="display: flex; align-items: center; justify-content: center; gap: 0.25rem; font-size: 0.74rem; font-weight: 800; color: #047857; background: rgba(16, 185, 129, 0.1); border: 1px solid rgba(16, 185, 129, 0.25); border-radius: var(--radius-pill); padding: 0.35rem 0.6rem; text-decoration: none;">
                        <span>Comprar ${stItems.length} ${stItems.length === 1 ? 'item' : 'itens'} no ${escapeHTML(stName)} &rarr;</span>
                      </a>
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
          </div>
        ` : ''}

        <!-- 2. Ranking de Custo Total por Estabelecimento -->
        <div class="chart-card mb-lg" style="margin-bottom: 1.25rem; padding: 1.15rem 1.25rem; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 1rem; box-shadow: var(--shadow-card);">
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.85rem;">
            <div style="display: flex; align-items: center; gap: 0.45rem;">
              <span class="material-symbols-outlined" style="font-size: 22px; color: var(--color-primary-text, #2563EB);">leaderboard</span>
              <h3 style="margin: 0; font-size: 1.05rem; font-weight: 800; color: var(--text-main);">
                Ranking de Estabelecimentos
              </h3>
            </div>
            <span style="font-size: 0.72rem; color: var(--text-muted); font-weight: 600;">
              ${ranking.length} ${ranking.length === 1 ? 'local cotado' : 'locais cotados'}
            </span>
          </div>

          <div style="display: flex; flex-direction: column; gap: 0.65rem;">
            ${ranking.map((store, idx) => {
              const isWinner = idx === 0;
              const pct = store.coveragePercent || 0;
              return `
                <div class="store-ranking-card" style="background: var(--bg-surface-alt); border: 1.5px solid ${isWinner ? '#10B981' : 'var(--border-color)'}; border-radius: 0.85rem; padding: 0.85rem 1rem; display: flex; flex-direction: column; gap: 0.45rem;">
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <div style="display: flex; align-items: center; gap: 0.45rem;">
                      <span style="font-size: 0.85rem; font-weight: 900; color: ${isWinner ? '#10B981' : 'var(--text-muted)'};">
                        ${isWinner ? '🏆 #1' : `#${idx + 1}`}
                      </span>
                      <strong style="font-size: 0.95rem; color: var(--text-main);">${escapeHTML(store.storeName)}</strong>
                    </div>
                    <span class="font-mono" style="font-size: 1.2rem; font-weight: 900; color: ${isWinner ? '#10B981' : 'var(--text-main)'};">
                      ${formatBRL(store.total)}
                    </span>
                  </div>

                  <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.75rem; color: var(--text-muted);">
                    <span>${store.itemsQuoted} de ${store.totalItems} produtos cotados (${pct}%)</span>
                    ${isWinner ? `<span style="color: #10B981; font-weight: 700;">Melhor Supermercado Único</span>` : ''}
                  </div>

                  <div style="height: 6px; background: var(--bg-surface-dim); border-radius: var(--radius-pill); overflow: hidden;">
                    <div style="height: 100%; width: ${pct}%; background: ${isWinner ? '#10B981' : 'var(--color-primary, #2563EB)'};"></div>
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>

        <!-- 3. Matriz Item a Item Completa -->
        ${multiData && multiData.itemsMatrix && multiData.itemsMatrix.length > 0 ? `
          <div class="chart-card" style="margin-bottom: 1.5rem; padding: 1.15rem 1.25rem; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 1rem; box-shadow: var(--shadow-card);">
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.85rem;">
              <div style="display: flex; align-items: center; gap: 0.45rem;">
                <span class="material-symbols-outlined" style="font-size: 22px; color: var(--color-primary-text, #2563EB);">table_chart</span>
                <h3 style="margin: 0; font-size: 1.05rem; font-weight: 800; color: var(--text-main);">
                  Matriz Comparativa Item a Item
                </h3>
              </div>
              <span style="font-size: 0.72rem; color: var(--text-muted); font-weight: 600;">
                ${multiData.itemsMatrix.length} itens
              </span>
            </div>

            <div style="display: flex; flex-direction: column; gap: 0.55rem;">
              ${multiData.itemsMatrix.map(row => {
                const desc = cleanProductDescription(row.item?.description || row.item?.descricao || 'Item');
                const catName = row.item?.categoryName || row.item?.categoriaNome || 'Outros';
                const icon = getCategoryIcon(catName);

                return `
                  <div style="background: var(--bg-surface-alt); border: 1px solid var(--border-color); border-radius: 0.85rem; padding: 0.85rem 1rem; display: flex; flex-direction: column; gap: 0.45rem;">
                    <!-- Row 1: Full-width Product Name with Category Icon -->
                    <div style="display: flex; align-items: flex-start; gap: 0.45rem; width: 100%;">
                      <span style="font-size: 1.25rem; line-height: 1.2; flex-shrink: 0;">${icon}</span>
                      <strong style="font-size: 0.95rem; font-weight: 800; color: var(--text-main); line-height: 1.35; word-break: break-word; flex: 1;">
                        ${escapeHTML(desc)}
                      </strong>
                    </div>

                    <!-- Row 2: Quantity + Lowest Price Summary below Product Name -->
                    <div style="display: flex; justify-content: space-between; align-items: center; padding: 0.25rem 0; font-size: 0.78rem;">
                      <span style="color: var(--text-muted);">
                        Quantidade: <strong style="color: var(--text-main); font-weight: 800;">${row.quantity}x</strong>
                      </span>
                      ${row.bestStore ? `
                        <div style="display: flex; align-items: center; gap: 0.35rem;">
                          <span style="color: #10B981; font-weight: 800; font-size: 0.75rem;">🏆 Menor:</span>
                          <strong class="font-mono" style="font-size: 0.92rem; color: #10B981;">${formatBRL(row.lowestPrice)}</strong>
                          <span style="color: var(--text-muted); font-size: 0.72rem;">no ${escapeHTML(formatStoreShortName(row.bestStore, 2))}</span>
                        </div>
                      ` : `
                        <span class="text-muted" style="font-size: 0.75rem;">Sem cotações</span>
                      `}
                    </div>

                    <!-- Row 3: Stores list stacked vertically one below another -->
                    ${Object.keys(row.pricesByStore).length > 0 ? `
                      <div style="display: flex; flex-direction: column; gap: 0.35rem; padding-top: 0.4rem; border-top: 1px dashed var(--border-color);">
                        ${Object.entries(row.pricesByStore).map(([st, pr]) => {
                          const isBest = st === row.bestStore;
                          const shortStore = formatStoreShortName(st, 3);
                          return `
                            <div style="background: ${isBest ? 'rgba(16, 185, 129, 0.12)' : 'var(--bg-surface)'}; border: 1.5px solid ${isBest ? '#10B981' : 'var(--border-color)'}; padding: 0.35rem 0.6rem; border-radius: 0.5rem; display: flex; justify-content: space-between; align-items: center; gap: 0.5rem;">
                              <span style="font-size: 0.76rem; font-weight: 700; color: ${isBest ? '#047857' : 'var(--text-muted)'}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" title="${escapeHTML(st)}">
                                ${escapeHTML(shortStore)}:
                              </span>
                              <div style="display: flex; align-items: center; gap: 0.25rem; flex-shrink: 0;">
                                <strong class="font-mono" style="font-size: 0.86rem; color: ${isBest ? '#10B981' : 'var(--text-main)'};">${formatBRL(pr)}</strong>
                                ${isBest ? `<span style="font-size: 0.75rem;">🏆</span>` : ''}
                              </div>
                            </div>
                          `;
                        }).join('')}
                      </div>
                    ` : ''}
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        ` : ''}

        <!-- Sticky Floating Bottom Navigation Bar -->
        <div style="position: fixed; bottom: 0; left: 0; right: 0; background: var(--bg-canvas); border-top: 1px solid var(--border-color); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); padding: 0.75rem 1rem; z-index: 50; display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; max-width: 768px; margin: 0 auto;">
          <a href="#lista-cotacao/${id}" class="btn" style="flex: 1; height: 42px; border-radius: var(--radius-pill); font-weight: 700; font-size: 0.85rem; border: 1px solid var(--border-color); background: var(--bg-surface-alt); color: var(--text-main); display: inline-flex; align-items: center; justify-content: center; gap: 0.35rem; text-decoration: none;">
            <span class="material-symbols-outlined" style="font-size: 18px; color: var(--color-primary-text, #2563EB);">photo_camera</span>
            <span>Voltar à Cotação</span>
          </a>
          
          <a href="#lista/${id}" class="btn btn-primary" style="flex: 1; height: 42px; border-radius: var(--radius-pill); font-weight: 700; font-size: 0.85rem; display: inline-flex; align-items: center; justify-content: center; gap: 0.35rem; text-decoration: none;">
            <span class="material-symbols-outlined" style="font-size: 18px;">shopping_cart</span>
            <span>Ir às Compras &rarr;</span>
          </a>
        </div>

      </div>
    `;

    container.querySelectorAll('.btn-open-store-split').forEach(btn => {
      btn.addEventListener('click', () => {
        const store = btn.getAttribute('data-store');
        if (store) {
          sessionStorage.setItem('active_list_store_filter', store);
        }
      });
    });
  } catch (err) {
    console.error('Erro ao renderizar comparativo:', err);
    container.innerHTML = `
      <div style="padding: 2rem; text-align: center; color: var(--text-muted);">
        Ocorreu um erro ao calcular o comparativo da lista.
      </div>
    `;
  }
}
